import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { FiInfo, FiPrinter } from 'react-icons/fi';
import { LuCheck } from 'react-icons/lu';

// Components
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import StatusBadge from '../../../components/StatusBadge';
import SerialNumberModal from '../../../components/modals/SerialNumberModal';
import HoldLineItemModal from '../../../components/modals/HoldLineItemModal';
import CancelLineItemModal from '../../../components/modals/CancelLineItemModal';
import PalletLabelModal from '../../../components/modals/PalletLabelModal';
import HoldInfoPopover from '../../../components/HoldInfoPopover';

// Services
import api from '../../../services/api';

const getReceivingServiceLabels = (instructions = {}) => {
  const labels = [];
  if (instructions.require_serial_numbers_on_receive) labels.push('Record serial numbers');
  if (instructions.require_mac_addresses_on_receive) labels.push('Record MAC addresses');
  if (instructions.relabel_on_receive) labels.push('Relabel on receipt');
  return labels;
};

const ReceivingScreen = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [globalBinLocation, setGlobalBinLocation] = useState('');
  
  // State for the Serial Number Modal
  const [isSnModalOpen, setIsSnModalOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const [selectedHoldLineItem, setSelectedHoldLineItem] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedCancelLineItem, setSelectedCancelLineItem] = useState(null);
  const [isPalletLabelModalOpen, setIsPalletLabelModalOpen] = useState(false);

  // Fetch request for receiving
  const { 
    data: receivingData, 
    isLoading, 
    error 
  } = useQuery(
    ['receiving', requestId],
    () => api.get(`/admin/receiving/${requestId}`),
    {
      enabled: !!requestId,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Error loading receiving data:', error);
      }
    }
  );

  // Quick receive line mutation
  const quickReceiveMutation = useMutation({
    mutationFn: ({ lineItemId, binLocation }) =>
      api.post(`/admin/receiving/${requestId}/line/${lineItemId}/quick-receive`, {
        binLocation
      }),
    onSuccess: (result, variables) => {
      // Find the line item that was just received to show its name
      const receivedLineItem = lineItems?.find(item => item.id === variables.lineItemId);
      const itemName = receivedLineItem?.productName || 'item';
      toast.success(`Successfully received ${result.data.qtyReceived} ${itemName} items`);
      queryClient.invalidateQueries(['receiving', requestId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error receiving items');
    }
  });

  // New mutation for receiving specific assets
  const receiveSpecificAssetsMutation = useMutation({
    mutationFn: ({ lineItemId, assets, binLocation, quantityToReceive, serialNumbers, macAddresses }) => 
      api.post(`/admin/receiving/${requestId}/line/${lineItemId}/receive-assets`, {
        assets: assets.map(a => a.id),
        binLocation,
        quantityOverride: quantityToReceive,
        serialNumbers,
        macAddresses
      }),
    onSuccess: (result) => {
      toast.success(result.data.message);
      queryClient.invalidateQueries(['receiving', requestId]);
      setIsSnModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error receiving specific assets');
    }
  });
  
  // Hold line mutation with reason
  const holdLineMutation = useMutation({
    mutationFn: ({ lineItemId, holdReason, holdQuantity, serialNumbers }) =>
      api.post(`/admin/receiving/${requestId}/line/${lineItemId}/hold`, {
        holdReason,
        holdQuantity,
        serialNumbers
      }),
    onSuccess: (result, variables) => {
      const heldLineItem = lineItems?.find(item => item.id === variables.lineItemId);
      const itemName = heldLineItem?.productName || 'item';
      toast.info(`Successfully placed ${itemName} on hold`);
      queryClient.invalidateQueries(['receiving', requestId]);
      setIsHoldModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error placing item on hold');
    }
  });

  // New mutation for placing assets on hold
  const holdAssetsMutation = useMutation({
    mutationFn: ({ lineItemId, assets }) => 
      api.post(`/admin/receiving/line/${lineItemId}/hold-assets`, {
        assetIds: assets.map(a => a.id)
      }),
    onSuccess: (result) => {
      toast.info(result.data.message);
      queryClient.invalidateQueries(['receiving', requestId]);
      setIsSnModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error placing items on hold');
    }
  });

  // New mutation for cancelling held items
  const cancelHeldItemsMutation = useMutation({
    mutationFn: ({ lineItemId, cancelReason, quantityToCancel }) =>
      api.post(`/admin/receiving/${requestId}/line/${lineItemId}/cancel`, { 
        cancelReason, 
        quantityToCancel 
      }),
    onSuccess: (result, variables) => {
      const cancelledLineItem = lineItems?.find(item => item.id === variables.lineItemId);
      const itemName = cancelledLineItem?.productName || 'item';
      toast.success(`Successfully cancelled ${itemName}`);
      queryClient.invalidateQueries(['receiving', requestId]);
      setIsCancelModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error cancelling held items');
    }
  });

  // New mutation for receiving items from hold
  const receiveFromHoldMutation = useMutation({
    mutationFn: ({ lineItemId, binLocation, quantityToReceive }) =>
      api.post(`/admin/receiving/${requestId}/line/${lineItemId}/receive-from-hold`, { 
        binLocation, 
        quantityToReceive 
      }),
    onSuccess: (result, variables) => {
      const receivedLineItem = lineItems?.find(item => item.id === variables.lineItemId);
      const itemName = receivedLineItem?.productName || 'item';
      toast.success(`Successfully received ${itemName} from hold`);
      queryClient.invalidateQueries(['receiving', requestId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error receiving items from hold');
    }
  });

  // New mutation for receiving specific assets from hold
  const receiveFromHoldSpecificMutation = useMutation({
    mutationFn: ({ lineItemId, assets, binLocation, quantityToReceive }) =>
      api.post(`/admin/receiving/${requestId}/line/${lineItemId}/receive-from-hold-assets`, {
        assets: assets.map(a => a.id), // Send only asset IDs
        binLocation,
        quantityToReceive
      }),
    onSuccess: (result) => {
      toast.success(result.data.message || 'Successfully received items from hold');
      queryClient.invalidateQueries(['receiving', requestId]);
      setIsSnModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error receiving specific items from hold');
    }
  });

  // Receive all lines mutation
  const receiveAllMutation = useMutation({
    mutationFn: (binLocation) =>
      api.post(`/admin/receiving/${requestId}/receive-all`, {
        binLocation
      }),
    onSuccess: (result) => {
      toast.success(`Successfully received all items. Request status: ${result.data.requestStatus}`);
      queryClient.invalidateQueries(['receiving', requestId]);
      // Navigate back to request detail after successful completion
      setTimeout(() => {
        navigate(`/admin/requests/${requestId}`);
      }, 2000);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error receiving all items');
    }
  });

  const handleQuickReceive = (lineItem, binLocation) => {
    if (!binLocation.trim()) {
      toast.error('Bin location is required');
      return;
    }

    const requiresIdentifierCapture =
      (lineItem.assets && lineItem.assets.length > 0) ||
      requestReceivingPreferences.require_serial_numbers_on_receive ||
      requestReceivingPreferences.require_mac_addresses_on_receive;

    if (requiresIdentifierCapture) {
      setSelectedLineItem({
        ...lineItem,
        binLocation,
        identifierRequirements: {
          requireSerialNumbers: requestReceivingPreferences.require_serial_numbers_on_receive,
          requireMacAddresses: requestReceivingPreferences.require_mac_addresses_on_receive
        }
      });
      setIsSnModalOpen(true);
    } else {
      quickReceiveMutation.mutate({ lineItemId: lineItem.id, binLocation });
    }
  };

  const handleQuickHold = (lineItem) => {
    setSelectedHoldLineItem(lineItem);
    setIsHoldModalOpen(true);
  };

  const handleConfirmHold = (holdData) => {
    holdLineMutation.mutate({
      lineItemId: selectedHoldLineItem.id,
      holdReason: holdData.holdReason,
      holdQuantity: holdData.holdQuantity,
      serialNumbers: holdData.serialNumbers
    });
  };

  const handleQuickCancel = (lineItem) => {
    setSelectedCancelLineItem(lineItem);
    setIsCancelModalOpen(true);
  };

  const handleConfirmCancel = (cancelReason, quantityToCancel) => {
    cancelHeldItemsMutation.mutate({
      lineItemId: selectedCancelLineItem.id,
      cancelReason,
      quantityToCancel
    });
  };

  const handleReceiveFromHold = (lineItem, binLocation) => {
    // For held items, we need to use the same flow as regular receiving
    // but filter to only show held assets
    if (!binLocation?.trim()) {
      toast.error('Bin location is required');
      return;
    }

    // Check if the line item has assets that require serial number scanning
    if (lineItem.assets && lineItem.assets.length > 0) {
      // Filter to only show assets that are on hold
      const heldAssets = lineItem.assets.filter(asset => asset.status === 'IN_HOLD');
      if (heldAssets.length > 0) {
        setSelectedLineItem({ 
          ...lineItem, 
          binLocation,
          assets: heldAssets, // Only show held assets in the modal
          isReceivingFromHold: true // Flag to indicate this is a receive-from-hold operation
        });
        setIsSnModalOpen(true);
      } else {
        toast.error('No assets are currently on hold for this item');
      }
    } else {
      // For non-asset items, proceed with direct receive from hold
      receiveFromHoldMutation.mutate({
        lineItemId: lineItem.id,
        binLocation: binLocation.trim()
      });
    }
  };
  
  const handleReceiveSpecific = ({ assets, quantityToReceive, serialNumbers, macAddresses }) => {
    if (selectedLineItem?.isReceivingFromHold) {
      receiveFromHoldSpecificMutation.mutate({
        lineItemId: selectedLineItem.id,
        assets,
        binLocation: selectedLineItem.binLocation,
        quantityToReceive: quantityToReceive
      });
    } else {
      receiveSpecificAssetsMutation.mutate({
        lineItemId: selectedLineItem.id,
        assets,
        binLocation: selectedLineItem.binLocation,
        quantityToReceive,
        serialNumbers,
        macAddresses
      });
    }
  };

  const handleHoldSpecific = (assetsToHold) => {
    holdAssetsMutation.mutate({
      lineItemId: selectedLineItem.id,
      assets: assetsToHold,
    });
  };

  const handleReceiveAll = () => {
    if (!globalBinLocation.trim()) {
      toast.error('Global bin location is required');
      return;
    }
    receiveAllMutation.mutate(globalBinLocation);
  };

  const getLineItemStatusBadge = (status) => {
    const statusColors = {
      'PENDING_RECEIVE': 'bg-yellow-100 text-yellow-800',
      'PARTIALLY_RECEIVED': 'bg-blue-100 text-blue-800',
      'PARTIALLY_RECEIVED_WITH_HOLD': 'bg-orange-100 text-orange-800',
      'IN_HOLD': 'bg-red-100 text-red-800',
      'RECEIVED': 'bg-green-100 text-green-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace(/_/g, ' ')}
      </span>
    );
  };

  // Derive data from the response — these must be above early returns
  // so the useMemo hook is called unconditionally (React rules of hooks).
  const responseData = receivingData?.data?.data;
  const request = responseData?.request;
  const lineItems = responseData?.lineItems;

  const uniquePallets = useMemo(() => {
    if (!lineItems) return [];
    const palletMap = new Map();
    for (const li of lineItems) {
      if (li.palletInfo?.palletId && !palletMap.has(li.palletInfo.palletId)) {
        palletMap.set(li.palletInfo.palletId, {
          palletId: li.palletInfo.palletId,
          palletLabel: li.palletInfo.palletLabel,
          itemCount: 0
        });
      }
      if (li.palletInfo?.palletId) {
        palletMap.get(li.palletInfo.palletId).itemCount += 1;
      }
    }
    return Array.from(palletMap.values());
  }, [lineItems]);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error.message || 'Error loading receiving data'} />;
  if (!receivingData?.data) return <ErrorAlert message="Receiving data not found" />;

  const requestReceivingPreferences = {
    require_serial_numbers_on_receive: !!request?.receivingPreferences?.require_serial_numbers_on_receive,
    require_mac_addresses_on_receive: !!request?.receivingPreferences?.require_mac_addresses_on_receive,
    relabel_on_receive: !!request?.receivingPreferences?.relabel_on_receive
  };

  const batchReceiveDisabled = requestReceivingPreferences.require_serial_numbers_on_receive || requestReceivingPreferences.require_mac_addresses_on_receive;
  const receivingServiceLabels = getReceivingServiceLabels(requestReceivingPreferences);

  // Additional safety check to ensure request object exists
  if (!request || !lineItems) {
    console.error('Data structure validation failed:', {
      hasRequest: !!request,
      hasLineItems: !!lineItems,
      lineItemsLength: lineItems ? lineItems.length : 'N/A',
      receivingData: receivingData.data
    });
    return <ErrorAlert message={`Invalid receiving data structure - Missing: ${!request ? 'request' : ''} ${!lineItems ? 'lineItems' : ''}`} />;
  }

  return (
    <div className="py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Receive Items - {request?.requestNumber || 'Loading...'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Process incoming inventory for warehouse: {request?.warehouse?.name || 'Unknown'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <StatusBadge status={request?.status} />
          {uniquePallets.length > 0 && (
            <button
              type="button"
              onClick={() => setIsPalletLabelModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiPrinter className="mr-2 h-4 w-4" />
              Print Pallet Labels
            </button>
          )}
          <Link
            to={`/admin/requests/${request?.id}`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Back to Request
          </Link>
        </div>
      </div>

      {/* Request Summary */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Request Summary
          </h3>
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Total Line Items</p>
              <p className="text-lg font-medium text-gray-900">{lineItems.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Items Fully Received</p>
              <p className="text-lg font-medium text-green-600">
                {lineItems.filter(item => item.status === 'RECEIVED').length}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Items Pending</p>
              <p className="text-lg font-medium text-yellow-600">
                {lineItems.filter(item => item.status === 'PENDING_RECEIVE').length}
              </p>
            </div>
          </div>
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-sm font-medium text-gray-700">Inbound Receiving Instructions</p>
            {receivingServiceLabels.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {receivingServiceLabels.map((label) => (
                  <span key={label} className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                    {label}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-500">No special receiving services requested for this ASN.</p>
            )}
          </div>
        </div>
      </div>

      {/* Global Actions */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Batch Actions
          </h3>
          <div className="mt-4 flex items-end space-x-4">
            <div className="flex-1">
              <label htmlFor="globalBinLocation" className="block text-sm font-medium text-gray-700">
                Global Bin Location
              </label>
              <input
                type="text"
                id="globalBinLocation"
                value={globalBinLocation}
                onChange={(e) => setGlobalBinLocation(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., A1-B2-C3"
              />
            </div>
            <button
              type="button"
              onClick={handleReceiveAll}
              disabled={receiveAllMutation.isLoading || !globalBinLocation.trim() || batchReceiveDisabled}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              {receiveAllMutation.isLoading ? 'Processing...' : 'Receive All Items'}
            </button>
          </div>
          {batchReceiveDisabled && (
            <p className="mt-3 text-sm text-amber-700">
              Batch receive is disabled for this customer because identifier capture is required on receiving.
            </p>
          )}
          {requestReceivingPreferences.relabel_on_receive && (
            <p className="mt-2 text-sm text-blue-700">
              Relabel service was requested for this ASN and should be completed during receiving.
            </p>
          )}
        </div>
      </div>

      {/* Line Items Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Line Items to Receive
          </h3>
        </div>
        <div className="border-t border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Packaging
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Warehouse Instructions
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lineItems.map(item => (
                  <LineItemRow 
                    key={item.id} 
                    lineItem={item} 
                    onQuickReceive={handleQuickReceive}
                    onQuickHold={handleQuickHold}
                    onQuickCancel={handleQuickCancel}
                    onReceiveFromHold={handleReceiveFromHold}
                    isReceiveLoading={quickReceiveMutation.isLoading && quickReceiveMutation.variables?.lineItemId === item.id}
                    isHoldLoading={holdLineMutation.isLoading && holdLineMutation.variables?.lineItemId === item.id}
                    isCancelLoading={cancelHeldItemsMutation.isLoading && cancelHeldItemsMutation.variables?.lineItemId === item.id}
                    isReceiveFromHoldLoading={receiveFromHoldMutation.isLoading && receiveFromHoldMutation.variables?.lineItemId === item.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
          
          {lineItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">No line items found for this request.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Serial Number Modal */}
      <SerialNumberModal
        isOpen={isSnModalOpen}
        onClose={() => setIsSnModalOpen(false)}
        lineItem={selectedLineItem}
        onReceive={handleReceiveSpecific}
        onHold={handleHoldSpecific}
        isLoading={receiveSpecificAssetsMutation.isLoading || holdAssetsMutation.isLoading || receiveFromHoldSpecificMutation.isLoading}
        identifierRequirements={selectedLineItem?.identifierRequirements}
      />

      {/* Hold Line Item Modal */}
      <HoldLineItemModal
        isOpen={isHoldModalOpen}
        lineItem={selectedHoldLineItem}
        onClose={() => setIsHoldModalOpen(false)}
        onConfirmHold={handleConfirmHold}
        isLoading={holdLineMutation.isLoading}
      />

      {/* Cancel Line Item Modal */}
      <CancelLineItemModal
        isOpen={isCancelModalOpen}
        lineItem={selectedCancelLineItem}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirmCancel={handleConfirmCancel}
        isLoading={cancelHeldItemsMutation.isLoading}
      />

      {/* Pallet Label Print Modal */}
      <PalletLabelModal
        isOpen={isPalletLabelModalOpen}
        onClose={() => setIsPalletLabelModalOpen(false)}
        requestId={requestId}
        pallets={uniquePallets}
      />
    </div>
  );
};

// Individual Line Item Row Component
const LineItemRow = ({ lineItem, onQuickReceive, onQuickHold, onQuickCancel, onReceiveFromHold, isReceiveLoading, isHoldLoading, isCancelLoading, isReceiveFromHoldLoading }) => {
  const [binLocation, setBinLocation] = useState('');

  const handleQuickReceiveClick = () => {
    onQuickReceive(lineItem, binLocation);
  };

  const handleQuickHoldClick = () => {
    onQuickHold(lineItem);
  };

  const handleQuickCancelClick = () => {
    onQuickCancel(lineItem);
  };

  const handleReceiveFromHoldClick = () => {
    onReceiveFromHold(lineItem, binLocation);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'PENDING_RECEIVE': 'bg-yellow-100 text-yellow-800',
      'PARTIALLY_RECEIVED': 'bg-blue-100 text-blue-800',
      'PARTIALLY_RECEIVED_WITH_HOLD': 'bg-orange-100 text-orange-800',
      'IN_HOLD': 'bg-red-100 text-red-800',
      'RECEIVED': 'bg-green-100 text-green-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">{lineItem.productName}</div>
          <div className="text-sm text-gray-500">{lineItem.productSku}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {lineItem.packagingType}
        {lineItem.palletInfo && (
          <div className="text-xs text-gray-400">
            Pallet: {lineItem.palletInfo.palletLabel}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {lineItem.palletInfo && (lineItem.palletInfo.depalletizeOnArrival || lineItem.palletInfo.crossDock) ? (
          <div className="space-y-1">
            {lineItem.palletInfo.depalletizeOnArrival && (
              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Depalletize on arrival
              </div>
            )}
            {lineItem.palletInfo.crossDock && (
              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Cross-dock immediately
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-xs">No special warehouse instructions</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="space-y-1">
          {/* Main quantity display */}
          <div className="font-medium text-gray-900">
            <span className="text-green-600">{lineItem.qtyReceived || 0}</span>
            <span className="text-gray-400 mx-1">/</span>
            <span>{lineItem.qtyOrdered}</span>
          </div>
          
          {/* Hold and remaining quantities */}
          <div className="text-xs space-y-0.5">
            {lineItem.qtyOnHold > 0 && (
              <div className="text-red-600 font-medium">
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                {lineItem.qtyOnHold} on hold
              </div>
            )}
            <div className="text-gray-500">
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
              {lineItem.qtyOrdered - (lineItem.qtyReceived || 0) - (lineItem.qtyOnHold || 0)} remaining
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
            <div className="relative h-full rounded-full overflow-hidden">
              {/* Received portion */}
              <div 
                className="absolute left-0 h-full bg-green-500 transition-all duration-300"
                style={{ 
                  width: `${((lineItem.qtyReceived || 0) / lineItem.qtyOrdered) * 100}%` 
                }}
              ></div>
              {/* Hold portion */}
              {lineItem.qtyOnHold > 0 && (
                <div 
                  className="absolute h-full bg-red-500 transition-all duration-300"
                  style={{ 
                    left: `${((lineItem.qtyReceived || 0) / lineItem.qtyOrdered) * 100}%`,
                    width: `${(lineItem.qtyOnHold / lineItem.qtyOrdered) * 100}%` 
                  }}
                ></div>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {getStatusBadge(lineItem.status)}
          {(lineItem.status === 'IN_HOLD' || lineItem.status === 'PARTIALLY_RECEIVED_WITH_HOLD') && lineItem.hold_reason && (
            <HoldInfoPopover
              holdReason={lineItem.hold_reason}
              holdDate={lineItem.hold_date}
              holdQuantity={lineItem.qtyOnHold}
              totalQuantity={lineItem.qtyOrdered}
              holdBy={lineItem.hold_by}
            />
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        {lineItem.status === 'RECEIVED' && (
          <span className="inline-flex items-center text-green-600 text-sm">
            <LuCheck className="h-4 w-4 mr-1" />
            Complete
          </span>
        )}
        {lineItem.status === 'IN_HOLD' && (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={binLocation}
              onChange={(e) => setBinLocation(e.target.value)}
              className="w-32 border border-gray-300 rounded-md shadow-sm py-1 px-2"
              placeholder="Bin Location"
            />
            <button
              onClick={handleReceiveFromHoldClick}
              disabled={isReceiveFromHoldLoading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {isReceiveFromHoldLoading ? '...' : 'Receive'}
            </button>
            <button
              onClick={handleQuickCancelClick}
              disabled={isCancelLoading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 disabled:opacity-50"
            >
              {isCancelLoading ? '...' : 'Cancel'}
            </button>
          </div>
        )}
        {lineItem.status !== 'RECEIVED' && lineItem.status !== 'IN_HOLD' && (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={binLocation}
              onChange={(e) => setBinLocation(e.target.value)}
              className="w-32 border border-gray-300 rounded-md shadow-sm py-1 px-2"
              placeholder="Bin Location"
            />
            <button
              onClick={handleQuickReceiveClick}
              disabled={isReceiveLoading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {isReceiveLoading ? '...' : 'Receive'}
            </button>
            <button
              onClick={handleQuickHoldClick}
              disabled={isHoldLoading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {isHoldLoading ? '...' : 'Hold'}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default ReceivingScreen; 
